import React, { useMemo, useState } from "react";
import {
  ARTIFACT_OVERLAY_LABELS,
  ARTIFACT_OVERLAY_TYPES,
  classifyChannelQuality,
  filterArtifactsByControls,
  type ArtifactOverlayType,
  type ClassifiedArtifact,
} from "../utils/artifactClassification";
import {
  ARTIFACT_QC_EXCLUDED_USE,
  ARTIFACT_QC_INTENDED_USE,
  applyReductionAction,
  buildImpedanceTimeline,
  createDefaultReductionState,
  createPendingQcSignOff,
  rejectQcSignOff,
  reductionViewLabel,
  signOffQc,
  summarizeElectrodeQuality,
  type ArtifactReductionAction,
  type ArtifactReductionState,
  type ArtifactReductionType,
  type ImpedanceTimelinePoint,
  type QcSignOffRecord,
} from "../utils/artifactQcTypes";
import {
  ARTIFACT_QC_IFU_UI_BADGE,
  ARTIFACT_QC_IFU_UI_SUMMARY,
} from "../utils/assistiveIfuFreeze";
import { ARTIFACT_REDUCTION_MODEL_CARD_URI } from "../utils/artifactReductionEngine";
import { EegDockPanelLabeling } from "./EegDockPanelLabeling";
import "./ArtifactOverlayControls.css";

export type ChannelQualityInput = {
  channel: string;
  snrDb?: number | null;
  impedanceKohms?: number | null;
  isBad?: boolean;
};

export type ArtifactOverlayControlsProps = {
  artifacts: ClassifiedArtifact[];
  channelQuality?: ChannelQualityInput[];
  impedanceSamples?: Array<{
    timeSeconds: number;
    channel: string;
    impedanceKohms: number;
  }>;
  overlayEnabled: boolean;
  onOverlayEnabledChange: (enabled: boolean) => void;
  confidenceThreshold: number;
  onConfidenceThresholdChange: (value: number) => void;
  enabledTypes: ArtifactOverlayType[];
  onEnabledTypesChange: (types: ArtifactOverlayType[]) => void;
  onSeek?: (timeSeconds: number) => void;
  onAttachEvidence?: (artifact: ClassifiedArtifact) => void;
  technologistId?: string | null;
  onQcSignOffChange?: (record: QcSignOffRecord) => void;
  onReductionAudit?: (action: ArtifactReductionAction) => void;
  onReductionStateChange?: (state: ArtifactReductionState) => void;
  modelCardUri?: string;
};

export default function ArtifactOverlayControls({
  artifacts,
  channelQuality = [],
  impedanceSamples = [],
  overlayEnabled,
  onOverlayEnabledChange,
  confidenceThreshold,
  onConfidenceThresholdChange,
  enabledTypes,
  onEnabledTypesChange,
  onSeek,
  onAttachEvidence,
  technologistId = "technologist",
  onQcSignOffChange,
  onReductionAudit,
  onReductionStateChange,
  modelCardUri = ARTIFACT_REDUCTION_MODEL_CARD_URI,
}: ArtifactOverlayControlsProps) {
  const [localSearch, setLocalSearch] = useState("");
  const [reduction, setReduction] = useState(createDefaultReductionState);
  const [auditTrail, setAuditTrail] = useState<ArtifactReductionAction[]>([]);
  const [qcSignOff, setQcSignOff] = useState<QcSignOffRecord>(
    createPendingQcSignOff,
  );
  const [qcNotes, setQcNotes] = useState("");

  const filtered = useMemo(
    () =>
      filterArtifactsByControls(artifacts, {
        enabledTypes,
        confidenceThreshold,
      }).filter((item) => {
        if (!localSearch.trim()) return true;
        const q = localSearch.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          item.type.includes(q) ||
          item.channels.some((ch) => ch.toLowerCase().includes(q))
        );
      }),
    [artifacts, enabledTypes, confidenceThreshold, localSearch],
  );

  const electrodeQuality = useMemo(
    () => summarizeElectrodeQuality(channelQuality, classifyChannelQuality),
    [channelQuality],
  );

  const impedanceTimeline: ImpedanceTimelinePoint[] = useMemo(
    () =>
      buildImpedanceTimeline(
        impedanceSamples.length
          ? impedanceSamples
          : channelQuality
              .filter((item) => item.impedanceKohms != null)
              .map((item, index) => ({
                timeSeconds: index * 60,
                channel: item.channel,
                impedanceKohms: Number(item.impedanceKohms),
              })),
      ),
    [impedanceSamples, channelQuality],
  );

  const toggleType = (type: ArtifactOverlayType) => {
    if (enabledTypes.includes(type)) {
      onEnabledTypesChange(enabledTypes.filter((item) => item !== type));
    } else {
      onEnabledTypesChange([...enabledTypes, type]);
    }
  };

  const recordReduction = (
    action: Parameters<typeof applyReductionAction>[1],
  ) => {
    const result = applyReductionAction(reduction, {
      ...action,
      actorId: technologistId,
      reducedTypes: (action.reducedTypes ||
        reduction.reducedTypes) as ArtifactReductionType[],
    });
    setReduction(result.state);
    setAuditTrail((prev) => [...prev, result.audit]);
    onReductionAudit?.(result.audit);
    onReductionStateChange?.(result.state);
  };

  const updateQc = (record: QcSignOffRecord) => {
    setQcSignOff(record);
    onQcSignOffChange?.(record);
  };

  return (
    <div className="artifact-overlay-controls" data-testid="artifact-overlay-controls">
      <EegDockPanelLabeling
        className="artifact-overlay-controls__intended-use"
        ariaLabel="Artifact QC intended use"
        data-testid="artifact-qc-intended-use"
        badge={ARTIFACT_QC_IFU_UI_BADGE}
        summary={ARTIFACT_QC_IFU_UI_SUMMARY}
        intendedUse={ARTIFACT_QC_INTENDED_USE}
        excludedUse={ARTIFACT_QC_EXCLUDED_USE}
        extraDetails={`Model card: ${modelCardUri}`}
      />

      <div
        className={`artifact-overlay-controls__reduction-banner${
          reduction.enabled && !reduction.showOriginalSignal ? " is-modified" : ""
        }`}
        data-testid="artifact-reduction-banner"
        role="status"
      >
        {reductionViewLabel(reduction)}
      </div>

      <div className="eeg-dock-panel-chrome__toolbar artifact-overlay-controls__toolbar">
        <label className="artifact-overlay-controls__toggle">
          <input
            type="checkbox"
            checked={overlayEnabled}
            onChange={(event) => onOverlayEnabledChange(event.target.checked)}
          />
          Artifact overlay
        </label>
        <label className="artifact-overlay-controls__toggle">
          <input
            type="checkbox"
            checked={reduction.enabled && !reduction.showOriginalSignal}
            aria-label="Enable selective artifact reduction"
            onChange={(event) => {
              if (event.target.checked) {
                recordReduction({
                  action: "enable_reduction",
                  actorId: technologistId,
                  reducedTypes: enabledTypes as ArtifactReductionType[],
                  showOriginalSignal: false,
                });
              } else {
                recordReduction({
                  action: "disable_reduction",
                  actorId: technologistId,
                  reducedTypes: [],
                  showOriginalSignal: true,
                });
              }
            }}
          />
          Selective reduction
        </label>
        <label className="artifact-overlay-controls__toggle">
          <input
            type="checkbox"
            checked={reduction.showOriginalSignal}
            aria-label="Show original unmodified signal"
            onChange={(event) => {
              recordReduction({
                action: event.target.checked ? "show_original" : "show_reduced",
                actorId: technologistId,
                reducedTypes: reduction.reducedTypes,
                showOriginalSignal: event.target.checked,
              });
            }}
          />
          Show original signal
        </label>
        <label className="artifact-overlay-controls__slider">
          Confidence {Math.round(confidenceThreshold * 100)}%
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(confidenceThreshold * 100)}
            aria-label="Artifact confidence threshold"
            onChange={(event) =>
              onConfidenceThresholdChange(Number(event.target.value) / 100)
            }
          />
        </label>
        <input
          className="artifact-overlay-controls__search"
          type="search"
          placeholder="Search artifacts"
          aria-label="Search artifacts"
          value={localSearch}
          onChange={(event) => setLocalSearch(event.target.value)}
        />
      </div>

      <div
        className="artifact-overlay-controls__filters"
        role="group"
        aria-label="Artifact type filter"
      >
        {ARTIFACT_OVERLAY_TYPES.map((type) => (
          <label key={type} className="artifact-overlay-controls__chip">
            <input
              type="checkbox"
              checked={enabledTypes.includes(type)}
              onChange={() => toggleType(type)}
            />
            {ARTIFACT_OVERLAY_LABELS[type]}
          </label>
        ))}
      </div>

      <div
        className="artifact-overlay-controls__impedance"
        aria-label="Electrode impedance timeline"
        data-testid="impedance-timeline"
      >
        <div className="artifact-overlay-controls__section-title">
          Electrode quality / impedance timeline
        </div>
        {impedanceTimeline.length === 0 && (
          <div className="artifact-overlay-controls__empty">
            No impedance samples yet.
          </div>
        )}
        <div className="artifact-overlay-controls__impedance-strip">
          {impedanceTimeline.slice(0, 120).map((point, index) => (
            <button
              key={`${point.channel}-${point.timeSeconds}-${index}`}
              type="button"
              className={`artifact-overlay-controls__z-point artifact-overlay-controls__z-point--${point.quality}`}
              title={`${point.channel} @ ${point.timeSeconds.toFixed(0)}s: ${point.impedanceKohms.toFixed(1)} kΩ`}
              aria-label={`Jump to impedance sample at ${point.timeSeconds.toFixed(0)} seconds for ${point.channel}`}
              onClick={() => onSeek?.(point.timeSeconds)}
            />
          ))}
        </div>
      </div>

      {electrodeQuality.length > 0 && (
        <div
          className="artifact-overlay-controls__quality"
          aria-label="Channel quality badges"
        >
          {electrodeQuality.map((item) => (
            <span
              key={item.channel}
              className={`artifact-overlay-controls__badge artifact-overlay-controls__badge--${item.quality}`}
              title={`${item.channel}: ${item.quality}${
                item.impedanceKohms != null
                  ? ` · ${item.impedanceKohms} kΩ`
                  : ""
              }`}
            >
              {item.channel}
            </span>
          ))}
        </div>
      )}

      <div
        className="artifact-overlay-controls__qc-signoff"
        data-testid="qc-signoff-panel"
      >
        <div className="artifact-overlay-controls__section-title">
          Technologist QC sign-off
        </div>
        <div className="artifact-overlay-controls__qc-status">
          Status: <strong>{qcSignOff.status}</strong>
          {qcSignOff.blocksPhysicianReporting
            ? " · blocks physician reporting"
            : " · reporting unlocked"}
        </div>
        <input
          type="text"
          value={qcNotes}
          aria-label="QC sign-off notes"
          placeholder="QC notes"
          onChange={(event) => setQcNotes(event.target.value)}
        />
        <div className="artifact-overlay-controls__qc-actions">
          <button
            type="button"
            aria-label="Sign off QC for physician reporting"
            onClick={() =>
              updateQc(
                signOffQc({
                  technologistId: technologistId || "technologist",
                  notes: qcNotes || null,
                }),
              )
            }
          >
            Sign off QC
          </button>
          <button
            type="button"
            aria-label="Reject QC sign-off"
            onClick={() =>
              updateQc(
                rejectQcSignOff({
                  technologistId: technologistId || "technologist",
                  notes: qcNotes || null,
                }),
              )
            }
          >
            Reject QC
          </button>
        </div>
      </div>

      <details className="artifact-overlay-controls__audit">
        <summary>Artifact-reduction audit ({auditTrail.length})</summary>
        {auditTrail.length === 0 ? (
          <div className="artifact-overlay-controls__empty">No reduction actions yet.</div>
        ) : (
          <ul>
            {auditTrail.map((entry) => (
              <li key={entry.actionId}>
                {entry.atIso}: {entry.action}
                {entry.reducedTypes.length
                  ? ` [${entry.reducedTypes.join(", ")}]`
                  : ""}
              </li>
            ))}
          </ul>
        )}
      </details>

      <div className="artifact-overlay-controls__list">
        {overlayEnabled &&
          filtered.map((artifact) => (
            <div key={artifact.id} className="artifact-overlay-controls__item">
              <button
                type="button"
                onClick={() => onSeek?.(artifact.startTimeSeconds)}
              >
                <strong>{ARTIFACT_OVERLAY_LABELS[artifact.type]}</strong>
                <span>
                  {artifact.startTimeSeconds.toFixed(1)}s ·{" "}
                  {Math.round(artifact.confidence * 100)}%
                </span>
                <span>{artifact.channels.join(", ") || "all channels"}</span>
              </button>
              {onAttachEvidence && (
                <button
                  type="button"
                  className="artifact-overlay-controls__evidence"
                  onClick={() => onAttachEvidence(artifact)}
                >
                  Attach evidence
                </button>
              )}
            </div>
          ))}
        {overlayEnabled && !filtered.length && (
          <div className="artifact-overlay-controls__empty">
            No artifacts match the current filters.
          </div>
        )}
        {!overlayEnabled && (
          <div className="artifact-overlay-controls__empty">
            Overlay disabled.
          </div>
        )}
      </div>
    </div>
  );
}
